import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components'
import { useTranslation } from 'react-i18next'
import type { Prompt } from '../../../types'

interface PromptSlashMenuProps {
  isOpen: boolean
  filtered: Prompt[]
  hasNoFavorites: boolean
  highlightedIndex: number
  onSelect: (prompt: Prompt) => void
}

const useStyles = makeStyles({
  menu: {
    // Anchored to the editor wrapper; its ancestors must stay overflow:visible so the menu can
    // cover the context bar and grow past the chat box.
    position: 'absolute',
    bottom: '100%',
    left: 0,
    minWidth: '160px',
    maxWidth: '80%',
    marginBottom: tokens.spacingVerticalXS,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow16,
    border: `1px solid ${tokens.colorTransparentStroke}`,
    maxHeight: '60vh',
    overflowY: 'auto',
    zIndex: 100,
    padding: tokens.spacingVerticalXS,
  },
  item: {
    display: 'block',
    width: '100%',
    padding: tokens.spacingVerticalSNudge,
    minHeight: tokens.spacingVerticalXXXL,
    border: 'none',
    backgroundColor: tokens.colorNeutralBackground1,
    cursor: 'pointer',
    textAlign: 'left',
    borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase300,

    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ':hover:active': {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
  },
  itemHighlighted: {
    backgroundColor: tokens.colorNeutralBackground1Hover,
  },
  emptyMessage: {
    padding: tokens.spacingVerticalSNudge,
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
})

export const PromptSlashMenu = ({
  isOpen,
  filtered,
  hasNoFavorites,
  highlightedIndex,
  onSelect,
}: PromptSlashMenuProps) => {
  const { t } = useTranslation()
  const classes = useStyles()

  if (!isOpen) return null

  return (
    <div role="listbox" aria-label={t('chatInput.promptMenu.label')} className={classes.menu}>
      {hasNoFavorites ? (
        <p className={classes.emptyMessage}>{t('prompts.empty.favorites.title')}</p>
      ) : (
        filtered.map((prompt, index) => (
          <button
            key={prompt.promptId}
            type="button"
            role="option"
            aria-selected={index === highlightedIndex}
            className={mergeClasses(
              classes.item,
              index === highlightedIndex && classes.itemHighlighted
            )}
            // Prevent editor blur before the click fires.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onSelect(prompt)}>
            {prompt.title}
          </button>
        ))
      )}
    </div>
  )
}
